import { JsonSchema } from '@sagold/react-json-editor';

import {
  METADATA_EXTRACTION_FROM_FILE_NAME_VALUE,
  METADATA_EXTRACTION_TYPE,
} from './constants';

type JsonSchemaProperty = NonNullable<JsonSchema['properties']>[string];
type JsonSchemaAllOfEntry = NonNullable<JsonSchema['allOf']>[number];
type JsonSchemaObjectAllOfEntry = Extract<JsonSchemaAllOfEntry, object>;

export type Step2ClassificationContext = {
  extractionMode: METADATA_EXTRACTION_TYPE;
  fileNameMetadataExtractionKey?: string;
};

/**
 * Checks whether an allOf entry conditionally enforces any of the supplied
 * property keys through its then/else branches.
 *
 * PoCo currently configures one conditional-required condition per target
 * metadata field, so the gateway emits one target key per allOf entry. Callers
 * can therefore include or exclude the complete entry. If the gateway later
 * groups multiple target keys into one entry, filtering callers must preserve
 * unrelated shared-field constraints instead of dropping the complete entry.
 */
export function allOfEntryTargetsAnyKey(
  entry: JsonSchemaAllOfEntry,
  keys: Set<string>
): entry is JsonSchemaObjectAllOfEntry {
  if (typeof entry !== 'object' || entry === null) {
    return false;
  }

  const thenProps =
    typeof entry.then === 'object' ? entry.then.properties : undefined;
  const elseProps =
    typeof entry.else === 'object' ? entry.else.properties : undefined;

  return Boolean(
    (thenProps && Object.keys(thenProps).some((key) => keys.has(key))) ||
    (elseProps && Object.keys(elseProps).some((key) => keys.has(key)))
  );
}

export function isPerFileInputInUploadProperty(
  property: JsonSchemaProperty | undefined
): boolean {
  return (
    typeof property === 'object' &&
    property.options?.perFileInputInUpload === true
  );
}

export function isSelectAllProperty(
  property: JsonSchemaProperty | undefined
): boolean {
  return typeof property === 'object' && property.options?.selectAll === true;
}

export function isFileNameExtractionProperty(
  key: string,
  fileNameMetadataExtractionKey?: string
): boolean {
  return (
    typeof fileNameMetadataExtractionKey === 'string' &&
    fileNameMetadataExtractionKey.length > 0 &&
    key === fileNameMetadataExtractionKey
  );
}

/**
 * Single switch for which schema properties belong on the validate (step-2)
 * editor. Add future step-2 flags here so schema generation and preFilter
 * stay in sync.
 */
export function belongsOnStep2(
  key: string,
  property: JsonSchemaProperty | undefined,
  ctx: Step2ClassificationContext
): boolean {
  if (typeof property !== 'object' || property == null) {
    return false;
  }

  if (isSelectAllProperty(property)) {
    return true;
  }

  if (ctx.extractionMode === METADATA_EXTRACTION_FROM_FILE_NAME_VALUE) {
    return isFileNameExtractionProperty(key, ctx.fileNameMetadataExtractionKey);
  }

  // fromAI and fromUser share the same step-2 fields
  return isPerFileInputInUploadProperty(property);
}

export function collectStep2PropertyKeys(
  jsonSchema: JsonSchema,
  ctx: Step2ClassificationContext
): Set<string> {
  return new Set(
    Object.keys(collectStep2Properties(jsonSchema, ctx).properties)
  );
}

export function collectStep2Properties(
  jsonSchema: JsonSchema,
  ctx: Step2ClassificationContext
): {
  properties: NonNullable<JsonSchema['properties']>;
  requiredKeys: string[];
} {
  const properties: NonNullable<JsonSchema['properties']> = {};
  const requiredKeys: string[] = [];

  if (!jsonSchema.properties) {
    return { properties, requiredKeys };
  }

  for (const [key, property] of Object.entries(jsonSchema.properties)) {
    if (belongsOnStep2(key, property, ctx)) {
      properties[key] =
        typeof property === 'object' ? { ...property } : property;
      if (jsonSchema.required?.includes(key)) {
        requiredKeys.push(key);
      }
    }
  }

  return { properties, requiredKeys };
}
